import {
  ActionDropdown,
  ActionDropdownTypes,
  Box,
  HStack,
  Icon,
  Stack,
  Text,
} from '@genial/components';
import { Color, Typography } from '@genial/design-tokens';
import { capitalize } from 'lodash';
import React from 'react';
import { RecoilState, useRecoilState } from 'recoil';

import { OrderByModalTypes } from '../OrderByModal';

interface OrderByStructureWithDirection extends OrderByModalTypes.OrderByStructure {
  direction: 'asc' | 'desc';
}

export declare namespace OrderByDropdownTypes {
  export interface Props {
    triggerLabel?: string;
    orderByAtom: RecoilState<OrderByModalTypes.OrderByAtom>;
    extraCustomBoxProps?: ActionDropdownTypes.Props['extraCustomBoxProps'];
  }

  export interface SelectedOrderByProps {
    fieldName: string;
  }
}

export const OrderByDropdown = ({
  orderByAtom,
  triggerLabel = 'Ordenar',
  extraCustomBoxProps,
}: OrderByDropdownTypes.Props) => {
  const [{ selectedOrderBy: _, availableOrderBy, appliedOrderBy }, setOrdersByState] =
    useRecoilState(orderByAtom);

  const handleCreateOptions = () => {
    const options: OrderByStructureWithDirection[] = [];

    availableOrderBy.forEach((item) => {
      const newItems = [
        { ...item, direction: 'asc' as const },
        { ...item, direction: 'desc' as const },
      ];
      options.push(...newItems);
    });
    return options;
  };

  const handleClearSort = () => {
    setOrdersByState((prev) => ({
      ...prev,
      appliedOrderBy: undefined,
    }));
  };

  const handleSort = (value: string, direction: 'asc' | 'desc') => {
    setOrdersByState((prev) => ({
      ...prev,
      appliedOrderBy: { direction, value },
    }));
  };

  const getLabel = (item: OrderByStructureWithDirection) => {
    const label = item.label?.toLocaleLowerCase();
    if (item.preventVariationLabel) {
      return capitalize(label);
    }
    const postLabel = item.direction === 'asc' ? '(crescente)' : '(decrescente)';
    return capitalize(`${label} ${postLabel}`);
  };

  const getCustomIcon = (item: OrderByStructureWithDirection) => {
    const getCustomChar = () => {
      if (item.customChar) {
        if (item.customChar.length === 2) {
          return item.direction === 'asc' ? item.customChar[0] : item.customChar[1];
        }
        return item.customChar[0];
      }
    };
    const char = getCustomChar() || item.label.charAt(0).toLocaleUpperCase();
    return (
      <HStack alignItems="center" w="inline.xxs">
        {!item.hideDefaultIcon && (
          <Icon
            name={item.direction === 'asc' ? 'ArrowUp' : 'ArrowDown'}
            size="xxxxs"
            color="brand.tertiary.principal"
            padding={0}
          />
        )}
        <Text color="brand.tertiary.principal">{char}</Text>
      </HStack>
    );
  };

  const renderOptions = () => {
    const options = handleCreateOptions();
    return (
      <>
        <ActionDropdown.Item
          label="Maior relevância"
          isActive={!appliedOrderBy}
          preventFocus
          onPress={handleClearSort}
        />
        {options.map((item) => (
          <ActionDropdown.Item
            key={item.value}
            label={getLabel(item)}
            isActive={
              appliedOrderBy?.value === item.value && appliedOrderBy?.direction === item.direction
            }
            customLeftIcon={getCustomIcon(item)}
            preventFocus
            onPress={() => handleSort(item.value, item.direction)}
          />
        ))}
      </>
    );
  };

  const hasSelection = appliedOrderBy !== undefined;

  return (
    <Stack position="relative">
      <ActionDropdown
        triggerType="secondary"
        triggerLabel={triggerLabel}
        triggerLabelColor="neutral.black"
        triggerIcon="Ordering"
        triggerIconProps={{
          size: 'xxxs',
          color: 'neutral.black',
        }}
        triggerProps={{
          paddingX: 'inline.xxxs',
          _text: { textTransform: 'none' },
        }}
        extraCustomBoxProps={{ padding: 0, ...extraCustomBoxProps }}
        triggerIconReplacesDropdownIcon
        placement="bottom right">
        {renderOptions()}
      </ActionDropdown>
      {hasSelection && (
        <Box
          w={Typography['fontsize.md']}
          h={Typography['fontsize.md']}
          borderRadius="full"
          background={Color['brand.tertiary.light']}
          position="absolute"
          top={0}
          right={0}
        />
      )}
    </Stack>
  );
};
